import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import DataTable from "../../components/common/DataTable";
import DatePicker from "../../components/common/DatePicker";
import DashboardActivity from "./dashboard";
import api from "../../services/api";
import { toast } from "react-toastify";

const MapIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);




function LoginActivityContent() {
  const LIMIT = 10;
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [mapModal, setMapModal] = useState(null);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowFormatMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dateError, setDateError] = useState("");
  const [pagination, setPagination] = useState(null);

  const normalizePagination = (raw, fallbackPage, fallbackLimit, rowCount) => {
    const p = raw || {};
    const current = Number(p.current_page ?? p.page) || fallbackPage;
    const total = Number(p.total) || 0;
    const lastByTotal = total > 0 ? Math.ceil(total / fallbackLimit) : 0;
    const last = Number(p.last_page ?? p.total_pages) || lastByTotal || (rowCount === fallbackLimit ? current + 1 : current);
    return {
      current_page: current,
      last_page: Math.max(1, last),
      total,
      has_more: Boolean(p.has_more ?? current < Math.max(1, last)),
    };
  };

  const validateDates = (from, to) => {
    if (from && to && new Date(from) > new Date(to)) {
      setDateError("From date cannot be after To date.");
    } else {
      setDateError("");
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearchQuery(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setLoading(true);
    api.get(`/activity/sessions`, {
      params: {
        page,
        limit: LIMIT,
        status: statusFilter === "all" ? undefined : statusFilter,
        search: searchQuery || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
      },
    })
      .then((res) => {
        const d = res.data?.data;
        const sessionsArray = d?.sessions || d?.data || d?.activity || (Array.isArray(d) ? d : []);
        const normalized = normalizePagination(d?.pagination || d?.meta, page, LIMIT, sessionsArray.length);
        setData(sessionsArray);
        setPagination(normalized);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [page, statusFilter, searchQuery, fromDate, toDate]);

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchQuery) params.append("search", searchQuery);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      params.append("format", format);
      const res = await api.get(`/activity/sessions/export?${params.toString()}`, { responseType: "blob" });
      const contentType = res.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const json = JSON.parse(text);
        toast.info(json.message || 'Export queued. Check Reports section to download.');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'csv';
      link.setAttribute("download", `activity_sessions_${new Date().toISOString().split("T")[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed successfully!');
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  
 const columns = [
    {
      accessorKey: "timestamp",
      header: "Date & Time",
      Cell: ({ cell }) => {
        const dt = cell.getValue() ? new Date(cell.getValue()) : null;
        return (<>
          <div>
            <p className="text-[14px] font-semibold text-[#191C1D]">{dt ? dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</p>
            <p className="text-[12px] text-[#4C444E] mt-0.5">{dt ? dt.toLocaleTimeString("en-US") : ""}</p>
          </div>
        </>);
      },
    },
    {
      accessorKey: "ip_address",
      header: "IP Address",
      Cell: ({ cell }) => (
        <span className="text-[13px] font-mono font-semibold text-[#475569] bg-slate-100 px-2.5 py-1 rounded-md">
          {cell.getValue() || "—"}
        </span>
      ),
    },
    {
      accessorKey: "action",
      header: "Action",
      Cell: ({ cell }) => {
        const m = cell.getValue();
        const color = m === "login" ? "text-emerald-600 bg-emerald-50" : m === "logout" ? "text-amber-600 bg-amber-50" : "text-blue-600 bg-blue-50";
        return <span className={`text-[12px] font-bold px-2.5 py-1 rounded-md ${color} capitalize`}>{m || "—"}</span>;
      },
    },
    {
      accessorKey: "method",
      header: "Method",
      Cell: ({ cell }) => (
        <span className="text-[12px] font-medium text-[#475569] capitalize">{cell.getValue() || "—"}</span>
      ),
    },
    {
      accessorKey: "city",
      header: "Location",
      Cell: ({ row }) => {
        const city = row.original.city;
        const country = row.original.country;
        if (!city && !country) return <span className="text-[12px] text-[#475569]">—</span>;
        return <span className="text-[12px] font-medium text-[#191C1D]">{[city, country].filter(Boolean).join(", ")}</span>;
      }
    },
    {
      id: "map",
      header: "GPS Map View",
      Cell: ({ row }) => (
        <div className="flex justify-start">
          <button
            onClick={() => {
              if (row.original.location) {
                setMapModal({ ...row.original.location, ip_address: row.original.ip_address });
              } else {
                setMapModal({ city: row.original.city, country: row.original.country, latitude: "N/A", longitude: "N/A", ip_address: row.original.ip_address });
              }
            }}
            className="flex items-center gap-1.5 text-[12px] font-semibold text-primary-mid hover:bg-primary-mid/5 px-3 py-1.5 rounded-lg border-none cursor-pointer transition-colors">
            <MapIcon />
            View Map
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
    
      {/* Header */}
      <div className="flex items-end justify-between mb-4">
            <div>
              <h1 className="text-[36px] font-bold text-primary tracking-tight">Login Activity</h1>
              <p className="text-[18px] font-regular text-[#4C444E] mt-1">Detailed audit logs for security monitoring.</p>
            </div>
            {/* Export Split Button */}
            <div className="relative" ref={menuRef}>
              <div className={`flex items-center rounded-lg overflow-hidden ${exporting ? 'opacity-60 pointer-events-none' : ''}`}>
                <button
                  onClick={() => handleExport('csv')}
                  disabled={exporting}
                  className="flex items-center gap-2 bg-[#4B2364] hover:bg-[#3a1a4f] px-4 py-[9px] text-[13px] font-semibold text-white cursor-pointer border-none transition-colors"
                >
                  {exporting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <DownloadIcon />}
                  {exporting ? 'Exporting...' : 'Export'}
                </button>
                <button
                  onClick={() => setShowFormatMenu((v) => !v)}
                  disabled={exporting}
                  className="flex items-center justify-center bg-[#4B2364] hover:bg-[#3a1a4f] px-2.5 h-[38px] text-white border-none cursor-pointer transition-colors"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className={`transition-transform duration-200 ${showFormatMenu ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </div>
              {showFormatMenu && (
                <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.12)] border border-gray-100 overflow-hidden w-[160px]">
                  <div className="px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100">Choose Format</div>
                  <button onClick={() => { handleExport('csv'); setShowFormatMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 cursor-pointer border-none bg-transparent transition-colors">
                    <span className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center shrink-0">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14,2 14,8 20,8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="16" y2="17" /></svg>
                    </span>
                    CSV
                  </button>
                  <button onClick={() => { handleExport('excel'); setShowFormatMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 cursor-pointer border-none bg-transparent transition-colors">
                    <span className="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14,2 14,8 20,8" /><rect x="8" y="12" width="8" height="6" rx="1" /></svg>
                    </span>
                    Excel
                  </button>
                </div>
              )}
            </div>
          </div>

     <div className="flex flex-wrap items-center gap-2 bg-[#F3F4F5] p-3 rounded-[12px] mb-6">
        <div className="flex items-center gap-2 bg-white rounded-[10px] px-3 py-2 min-w-[240px]">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search..."
            className="bg-transparent outline-none text-[13px] text-[#191C1D] w-full"
          />
        </div>

        <div className={`flex items-center gap-2 bg-white rounded-[10px] px-3 py-2 ${dateError ? "ring-1 ring-red-400" : ""}`}>
          <span className="text-[11px] font-semibold text-[#4C444E] uppercase">FROM</span>
          <DatePicker
            value={fromDate}
            onChange={(e) => {
              const val = e.target.value;
              setFromDate(val);
              validateDates(val, toDate);
              setPage(1);
            }}
            placeholder="mm/dd/yyyy"
            className="w-[105px]"
          />
        </div>

        <div className={`flex items-center gap-2 bg-white rounded-[10px] px-3 py-2 ${dateError ? "ring-1 ring-red-400" : ""}`}>
          <span className="text-[11px] font-semibold text-[#4C444E] uppercase">TO</span>
          <DatePicker
            value={toDate}
            onChange={(e) => {
              const val = e.target.value;
              setToDate(val);
              validateDates(fromDate, val);
              setPage(1);
            }}
            placeholder="mm/dd/yyyy"
            className="w-[105px]"
          />
        </div>
        {dateError && <span className="text-[11px] text-red-500 font-medium ml-1">{dateError}</span>}
      </div>

      {/* Table */}
       <div className="bg-white rounded-[32px] shadow-[0px_1px_2px_0px_#0000000D] overflow-hidden">
            <DataTable
              columns={columns}
              data={data}
              muiTablePaperProps={{
                sx: {
                  boxShadow: "none",
                  border: "none",
                },
              }}
            />
            {/* Pagination */}
            {pagination && pagination.last_page > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <span className="text-xs text-[#4C444E]">Page {page} of {pagination.last_page}</span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer transition-colors ${page === 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>
                  {Array.from({ length: Math.min(pagination.last_page, 5) }, (_, i) => {
                    let n = i + 1;
                    if (pagination.last_page > 5 && page > 3) n = page - 2 + i;
                    if (n > pagination.last_page) return null;
                    return (
                    <button key={n}
                      onClick={() => setPage(n)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
                        page === n ? 'bg-primary text-white border border-primary' : 'bg-white text-[#4C444E] border border-[#CEC3D0] hover:bg-gray-50'
                      }`}
                    >{n}</button>
                  );
                  })}
                  {pagination.last_page > 5 && <span className="text-[#4C444E] text-xs px-1">...</span>}
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!pagination.has_more}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg border border-[#CEC3D0] bg-white text-[#4C444E] hover:bg-gray-50 cursor-pointer transition-colors ${!pagination.has_more ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

      {/* Map Modal */}
      {mapModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

            {/* Header */}
            <div className="relative bg-primary-gradient px-6 py-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-bold text-[30px]">GPS Location</h3>
                  </div>
                </div>
                <button
                  onClick={() => setMapModal(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 cursor-pointer border-none transition-colors mt-0.5"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Info Pills */}
              <div className="gap-2 mt-1">
                <div >
                  <span className="text-[#F3E8FFB2] text-[16px] font-medium">Login IP: {mapModal.ip_address}</span>
                </div>
                <div>
                  <span className="text-[#F3E8FFB2] text-[16px] font-medium">Response Time: {mapModal.response_time_ms} ms</span>
                </div>
                <div>
                  <span className="text-[#F3E8FFB2] text-[16px] font-medium">Latitude/Longitude: {mapModal.latitude}, {mapModal.longitude}</span>
                </div>
              </div>
            </div>

            {/* Map Preview */}
           <div className="relative mx-4 my-4 rounded-xl overflow-hidden shadow-sm h-48">
            <iframe
              title="map"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              src={`https://www.google.com/maps?q=${mapModal.latitude},${mapModal.longitude}&z=15&output=embed`}
              ></iframe>
            </div>

          </div>
        </div>
      )}
    </>
  );
}

export default function Activity() {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "loginActivity";

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-w-0">
      <div className="flex-1 px-8 py-6 overflow-y-auto">
        {tab === "DashboardActivity" ? <DashboardActivity /> : <LoginActivityContent />}
      </div>
    </div>
  );
}
